import { FC, ReactNode } from 'react';
import { makeStyles, Theme, ButtonBase } from '@material-ui/core';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import clsx from 'clsx';
import { useWbTabsContext } from './WbTabsContext';
import { TabBase } from './types';

const useStyles = makeStyles<Theme, { iconBreakpoint: Breakpoint }>(
  (theme: Theme) => ({
    root: {
      color: theme.palette.primary.main,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
      borderRadius: theme.spacing(0.75),
      fontSize: theme.typography.pxToRem(14),
      fontWeight: 500,
      transition: 'all 0.15s ease',
      padding: theme.spacing(0.75, 1.5),
      position: 'relative',
      zIndex: 1,
      cursor: 'pointer',
      border: 'none',
      backgroundColor: 'transparent',
      '&:hover': {
        color: theme.palette.text.primary,
      },
      '&:disabled': {
        pointerEvents: 'none',
        opacity: 0.5,
      },
    },
    selected: {
      '@supports not (anchor-name: --a)': {
        backgroundColor: theme.palette.common.white,
        borderRadius: 12,
        boxShadow: theme.shadows[1],
      },
    },
    tabContainer: {
      display: 'flex',
      gap: theme.spacing(0.5),
      alignItems: 'center',
    },
    titleResponsive: props => ({
      [theme.breakpoints.down(props.iconBreakpoint)]: {
        display: 'none',
      },
    }),
  }),
  { name: 'WbViewTabItem' },
);

type WbViewTabItemProps = TabBase &
  (
    | {
        disabled?: boolean;
        className?: string;
        icon?: ReactNode;
        title: string;
        'aria-label'?: string;
      }
    | {
        disabled?: boolean;
        className?: string;
        icon?: ReactNode;
        title?: never;
        'aria-label': string;
      }
  );

export const WbViewTabItem: FC<WbViewTabItemProps> = ({
  value,
  disabled,
  className,
  icon,
  title,
  'aria-label': ariaLabel,
}) => {
  const {
    value: selectedValue,
    onValueChange,
    iconBreakpoint,
    instanceId,
    getPanelId,
    getTabId,
  } = useWbTabsContext();
  const classes = useStyles({ iconBreakpoint });
  const isSelected = selectedValue === value;

  const handleClick = () => {
    if (!disabled) {
      onValueChange(value);
    }
  };

  return (
    <ButtonBase
      role="tab"
      id={getTabId(value)}
      aria-selected={isSelected}
      aria-controls={getPanelId(value)}
      aria-label={ariaLabel ?? title}
      tabIndex={isSelected ? 0 : -1}
      data-value={value}
      disabled={disabled}
      onClick={handleClick}
      className={clsx(
        classes.root,
        {
          [classes.selected]: isSelected,
        },
        className,
      )}
      style={
        { anchorName: `--wb-tab-${instanceId}-${value}` } as React.CSSProperties
      }
    >
      <div className={classes.tabContainer}>
        {icon}
        {title && (
          <span className={icon ? classes.titleResponsive : undefined}>
            {title}
          </span>
        )}
      </div>
    </ButtonBase>
  );
};
